import {
	PredefinedConfiguration,
	MissingProductItem,
	SofaConfigurationItem,
	RangeSettings,
	CoverItem,
} from 'sofa-reborn-react';
import { ThunkResult, RootState, AppDispatch } from '../..';

import { setCurrentLightbox, setAppView, resetIframeHeight, setHasVisitScene } from '../actions';
import { setMissingCover } from '../../cover/actions';
import { setNoSummaryPageBackButton } from '../../checkout/actions';
import { setFromNavigator } from '../actions';

import {
	setSelectedConfiguration,
	setPersistedConfiguration,
	setPredefinedConfigurationId,
	setMissingConfiguration,
	setMissingDeeplink,
} from '../../sofaConfiguration';
import { UrlService } from '../../../services';
import { setReplaceCoverItems } from '../../cover';
import { ProductUtils } from '../../../util/ProductUtils';
import ThunksUtil from '../utils/ThunksUtil';

import { ProductData } from '@ikea-aoa/component-view-3d';

export default class ConfigurationThunk {
	public set(
		predefinedConfiguration: PredefinedConfiguration | null,
		isBlank: boolean,
		isPreviousConfiguration: boolean
	): ThunkResult {
		return (dispatch, getState): void => {
			const state = getState();
			const { hasMissingAutoConnectProduct, hasMissingProduct } = this.hasMissing(
				state,
				predefinedConfiguration
			);

			if (hasMissingAutoConnectProduct || hasMissingProduct) {
				this.handleMissing(state, dispatch, predefinedConfiguration);
			} else {
				if (predefinedConfiguration) {
					this.handlePredefinedConfiguration(
						state,
						dispatch,
						isPreviousConfiguration,
						predefinedConfiguration
					);
				} else {
					this.handleNoPredefinedConfiguration(dispatch, isBlank);
				}
			}
			this.handleFromNavigator(dispatch);
		};
	}

	private handleNoPredefinedConfiguration(dispatch: AppDispatch, isBlank: boolean): void {
		dispatch(setMissingCover(false));
		if (!isBlank) {
			const id = UrlService.getVpcOrSprId();

			if (id) {
				// Show article missing lightbox with start from scratch and go to PIG
				dispatch(setCurrentLightbox('missing-article'));
				dispatch(setMissingDeeplink(id));
			} else {
				dispatch(setCurrentLightbox('product-intro-gallery'));
			}

			dispatch(setAppView('build-view'));
			dispatch(setPredefinedConfigurationId('blank'));
			dispatch(setPersistedConfiguration(null));
			/* Remove vpc from url */
			const newHash = UrlService.getReplacedIdHash('blank');
			UrlService.setBrowserWindowHash(newHash);
		} else {
			dispatch(setHasVisitScene(true));
		}
		dispatch(setSelectedConfiguration({ configuration: {} }));
	}

	private handlePredefinedConfiguration(
		state: RootState,
		dispatch: AppDispatch,
		isPreviousConfiguration: boolean,
		predefinedConfiguration: PredefinedConfiguration
	): void {
		/* Check if configuration only include old ids, then show missing article lightbox */
		let onlyContainsOldIds = false;
		if (!isPreviousConfiguration) {
			if (predefinedConfiguration.configuration.children) {
				const { products, missingProducts } = state.products;
				onlyContainsOldIds = this.onlyContainOldIds(
					products,
					missingProducts,
					predefinedConfiguration.configuration.children
				);
			}
			dispatch(resetIframeHeight());
			const showSummaryPage = state.checkout.noSummaryPageBackButton || state.checkout.showSummary;
			if (!showSummaryPage) {
				dispatch(setAppView('build-view'));
			}
			dispatch(setMissingCover(false));
		}
		if (!onlyContainsOldIds) {
			dispatch(setSelectedConfiguration(predefinedConfiguration));
		} else {
			// Show article missing lightbox with start from scratch and go to PIG
			dispatch(setCurrentLightbox('missing-article'));
			dispatch(setSelectedConfiguration({ configuration: {} }));
		}
		dispatch(setHasVisitScene(true));
	}

	private handleMissing(
		state: RootState,
		dispatch: AppDispatch,
		predefinedConfiguration: PredefinedConfiguration | null
	): void {
		const { rangeSettings } = state.settings;
		if (state.checkout.noSummaryPageBackButton || state.checkout.showSummary) {
			dispatch(setCurrentLightbox('no-valid-link'));
		} else {
			if (
				rangeSettings &&
				predefinedConfiguration?.configuration.children &&
				predefinedConfiguration.configuration.children.length > 0
			) {
				const { products, missingProducts } = state.products;
				const configurationModelIds = ProductUtils.getModelIdsForConfiguration(
					products,
					missingProducts,
					predefinedConfiguration.configuration.children as SofaConfigurationItem[],
					rangeSettings.shouldColorAllModules as boolean,
					false
				);

				const availableCovers = ProductUtils.getAllAvailableCovers(
					products,
					state.cover.coverItems,
					configurationModelIds
				);

				if (availableCovers.length > 0) {
					this.handleMissingCover(dispatch, predefinedConfiguration, availableCovers);
				} else {
					this.handleNoCovers(
						dispatch,
						rangeSettings,
						products,
						missingProducts,
						predefinedConfiguration
					);
				}
				if (state.checkout.noSummaryPageBackButton) {
					dispatch(setNoSummaryPageBackButton(false));
				}
				dispatch(setHasVisitScene(true));
			}
		}
	}

	private handleNoCovers(
		dispatch: AppDispatch,
		rangeSettings: RangeSettings,
		products: ProductData,
		missingProducts: MissingProductItem[],
		predefinedConfiguration: PredefinedConfiguration | null
	): void {
		dispatch(setMissingCover(false));
		if (rangeSettings.shouldColorAllModules) {
			dispatch(setSelectedConfiguration({ configuration: {} }));
		} else if (predefinedConfiguration?.configuration.children) {
			const newConfiguration = ProductUtils.getNewConfiguration(
				products,
				missingProducts,
				predefinedConfiguration.configuration.children,
				true,
				rangeSettings.shouldColorAllModules as boolean
			);

			/* The new configuration does not contain anything, create empty configuration */
			if (newConfiguration.length === 0) {
				predefinedConfiguration = { configuration: {} };
			} else {
				predefinedConfiguration.configuration.children = newConfiguration;
			}

			dispatch(setSelectedConfiguration(predefinedConfiguration));
		}

		// Show article missing lightbox with start from scratch or use this combination and go to PIG
		dispatch(setCurrentLightbox('missing-article'));
	}

	private handleMissingCover(
		dispatch: AppDispatch,
		predefinedConfiguration: PredefinedConfiguration,
		availableCovers: CoverItem[]
	): void {
		dispatch(setMissingConfiguration(predefinedConfiguration));
		dispatch(resetIframeHeight());
		dispatch(setAppView('build-view'));
		dispatch(setReplaceCoverItems(availableCovers));
		dispatch(setMissingCover(true));
		dispatch(setCurrentLightbox('cover-picker'));
		dispatch(setSelectedConfiguration({ configuration: {} }));
	}

	private hasMissing(state: RootState, predefinedConfiguration: PredefinedConfiguration | null) {
		let hasMissingAutoConnectProduct = false;
		let hasMissingProduct = false;
		if (
			predefinedConfiguration?.configuration.children &&
			predefinedConfiguration.configuration.children.length > 0
		) {
			const { products, missingProducts } = state.products;
			const { rangeSettings } = state.settings;
			const configurationChildren = predefinedConfiguration.configuration.children;
			if (rangeSettings) {
				hasMissingAutoConnectProduct =
					rangeSettings.addAutomatically || rangeSettings.addArmrestsAutomatically
						? ThunksUtil.hasMissingAutoConnectProduct(
								rangeSettings,
								products,
								missingProducts,
								configurationChildren
						  )
						: false;
			}

			if (!hasMissingAutoConnectProduct) {
				hasMissingProduct = ThunksUtil.containsMissingProduct(
					products,
					missingProducts,
					configurationChildren
				);
			}
		}
		return { hasMissingAutoConnectProduct, hasMissingProduct };
	}

	private handleFromNavigator(dispatch: AppDispatch): void {
		const params = UrlService.getApplicationParams();
		const fromNavigator = params.fromNavigator;

		if (fromNavigator) {
			dispatch(setFromNavigator(fromNavigator));
		}
	}

	private onlyContainOldIds(
		products: ProductData,
		missingProducts: MissingProductItem[],
		sofaConfigurationItems: SofaConfigurationItem[]
	): boolean {
		let onlyOldIds = false;
		for (let i = 0; i < sofaConfigurationItems.length; i++) {
			const includedInProducts =
				Object.keys(products).find(
					(product) =>
						products[product].productId === (sofaConfigurationItems[i].articleId as string)
				) !== undefined;
			const includedInMissingProducts = missingProducts.some(
				(product) =>
					product.productId === sofaConfigurationItems[i].articleId ||
					product.previousId === sofaConfigurationItems[i].articleId
			);
			if (!includedInProducts && !includedInMissingProducts) {
				onlyOldIds = true;
				break;
			}
		}

		return onlyOldIds;
	}
}
